import { HttpTypes } from "@medusajs/types"

export type StockReading = {
  /** null when the line isn't stock-tracked: say nothing rather than "sold out". */
  label: string | null
  tone: "ok" | "low" | "out" | "backorder"
  available: number
  /** true when units beyond `available` can be ordered as a $0 backorder (backend lib/backorder.ts) */
  backorder: boolean
}

/** Plenty above this many units; below it we always show the number (Jayson: never "precise but wrong"). */
const PLENTY = 20

/**
 * Same stock modes as the backend (lib/backorder.ts), 5 Nov 2026 ("we need to be accurate"):
 *   made       Teelixir (K-*) that Teelixir can make more of: "In stock".
 *   held       short-dated (-SDmmyy): sells what's on hand, then "Currently unavailable".
 *   backorder  everything else: when none are left, "Backorder" (ordered at $0, emailed when in stock).
 */
export const stockMode = (v: any): "untracked" | "made" | "held" | "backorder" => {
  if (v?.manage_inventory === false) return "untracked"
  if (/-SD\d{4}$/i.test(String(v?.sku ?? ""))) return "held"
  const short = v?.metadata?.stock_short === true || v?.metadata?.stock_short === "true"
  if (/^K-/.test(String(v?.sku ?? "")) && v?.allow_backorder && !short) return "made"
  return "backorder"
}

export const variantStock = (variant: HttpTypes.StoreProductVariant): StockReading => {
  const mode = stockMode(variant)
  if (mode === "untracked") return { label: null, tone: "ok", available: 0, backorder: false }
  const available = Math.max(0, Number((variant as any).inventory_quantity ?? 0))
  const backorder = mode === "backorder"
  if (available <= 0) {
    if (mode === "made") return { label: "In stock", tone: "ok", available: 0, backorder: false }
    if (backorder) return { label: "Backorder: not charged now, we'll email you when it's in", tone: "backorder", available: 0, backorder: true }
    return { label: "Currently unavailable", tone: "out", available: 0, backorder: false }
  }
  if (mode !== "made" && available < PLENTY) return { label: `Only ${available} left${backorder ? ", more on backorder" : ""}`, tone: "low", available, backorder }
  return { label: "In stock", tone: "ok", available, backorder }
}

/** Units of `quantity` that would go on backorder for this variant (0 when it's all in stock or not backorderable). */
export const backorderUnits = (variant: any, quantity: number) => {
  const s = variantStock(variant)
  return s.backorder ? Math.max(0, quantity - s.available) : 0
}

export const stockClass = (tone: StockReading["tone"]) =>
  tone === "out" ? "text-red-600" : tone === "low" || tone === "backorder" ? "text-elevate-gold-dark" : "text-elevate-grey-mid"
